import * as React from "react";
import { EventVendorQuery } from "@src/@types/api.d";
import { Mail } from "lucide-react";
import { motion } from "framer-motion";
import Icon from "@src/components/Icon";
import { decodeHtmlEntities } from "@src/utils/general";

type Props = {
  eventId: number;
  eventVendorData: EventVendorQuery;
};

const Vendors: React.FC<Props> = ({ eventVendorData }) => {
  const vendors = (eventVendorData?.eventVendor ?? []).filter(
    (vendor): vendor is NonNullable<typeof vendor> => Boolean(vendor)
  );

  if (!vendors.length) {
    return null;
  }

  return (
    <section className="w-full py-10 md:py-16 bg-[#FAF7F5] relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,139,34,0.08),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(28,24,32,0.05),transparent_28%)]" />
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-20">
        <div className="mb-12 text-center relative z-10 md:mb-16">
          <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
            Creative Partners
          </span>
          <h3 className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]">
            Our <em className="font-light italic text-[#228B22]">Vendors</em>
          </h3>
          <div className="mt-5 flex items-center justify-center gap-4">
            <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
            <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
            <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
          </div>
        </div>

        <article className="border border-[#228B22]/40 p-2 relative z-10">
          <div className="bg-[#FAF7F5]/95 border border-[#228B22]/40 shadow-[0_24px_70px_rgba(28,24,32,0.08)] p-6 md:p-8">
            <div className="grid grid-cols-1 gap-4">
              {vendors.map((vendor, index) => {
                const decodedName = decodeHtmlEntities(vendor.name);
                const decodedRole = decodeHtmlEntities(vendor.role);
                const decodedEmail = decodeHtmlEntities(vendor.email);
                const decodedInstagramHandle = decodeHtmlEntities(
                  vendor.instagram_handle
                ).trim();
                const normalizedHandle = decodedInstagramHandle
                  .replace(/^@/, "")
                  .replace(/^https?:\/\/www\.instagram\.com\//i, "")
                  .replace(/\/$/, "");
                const instagramHref = normalizedHandle
                  ? `https://www.instagram.com/${normalizedHandle}/`
                  : "";

                return (
                  <div
                    key={`vendors-home-page-${index}`}
                    className="w-full p-5 md:p-6 border border-[#228B22]/18 bg-[#FAF7F5]"
                  >
                    <div className="font-corGaramond text-2xl md:text-3xl text-[#401d15] leading-tight">
                      {decodedName}
                    </div>

                    <p className="mt-1 font-jost font-medium text-xs md:text-sm text-[#228B22] uppercase tracking-widest">
                      {decodedRole}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[#401d15]/65">
                      {!!decodedEmail && (
                        <p className="font-jost font-normal text-sm md:text-base text-[#401d15]/65 inline-flex items-center gap-1.5 break-all">
                          <Mail
                            className="w-4 h-4 text-[#228B22]"
                            aria-hidden="true"
                          />
                          {decodedEmail}
                        </p>
                      )}

                      {!!instagramHref && (
                        <a
                          target="_blank"
                          href={instagramHref}
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[#401d15]/65 hover:text-[#228B22] transition-colors"
                        >
                          <span
                            className="w-5 h-5 inline-flex items-center justify-center flex-shrink-0 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:block text-[#228B22]"
                            aria-hidden="true"
                          >
                            <Icon iconName="instagram" />
                          </span>
                          <span className="font-jost text-sm md:text-base">
                            {decodedInstagramHandle}
                          </span>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default Vendors;
